const KB = 1024;
const MB = KB * KB;
const GB = MB * KB;

export const ROOT_LABEL = "FTP";
export const OVERRIDE_REASON_MIN = 10;
export const OVERRIDE_REASON_MAX = 500;
export const COLUMNS_PREVIEW_LIMIT = 10;

export const FLOWS = {
    roster: {
        title: "Build Universal Roster",
        building: "Building the universal roster",
        intro: "Open the drop folder, then check the file chosen for each object. You can change, add or remove files.",
        unavailable: "Building the universal roster isn't available for this account or your role.",
    },
    participation: {
        title: "Build Plan Participation",
        building: "Building plan participation",
        intro: "Tick the files to merge, opening folders as you need to. Your ticks stay as you move between folders.",
        unavailable: "Building plan participation isn't available for this account or your role.",
    },
};

// `timing` is the stage_seconds entry the build writes when that stage ends.
const stagesWith = (building) => [
    { id: "queued", label: "Waiting to start", timing: null },
    { id: "download", label: "Downloading the files", timing: "download" },
    { id: "build", label: building, timing: "build_and_raw_copies" },
    { id: "save", label: "Saving the results", timing: "outputs" },
    { id: "headers", label: "Checking the file header", timing: "header_check" },
];

export const BUILD_STAGES = {
    roster: stagesWith("Building the roster"),
    participation: stagesWith("Merging the files"),
};

const stagesOf = (build) => BUILD_STAGES[build?.kind] || BUILD_STAGES.roster;

const SUBMIT_NOTES = {
    shadow: "This account is in shadow stage, so builds can be reviewed but not submitted.",
    switchedOff: "Submitting is switched off right now.",
    role: "Only an Operation Head can submit.",
    build: "This build can't be submitted.",
};

const PHASE_ANNOUNCEMENTS = {
    loading: "Loading.",
    error: "Couldn't load. You can retry.",
    expired: "Your session has expired. Sign in again to continue.",
};

const STEP_ANNOUNCEMENTS = {
    roster: {
        choose: "Choose a file for each object.",
        building: "Building the roster.",
        build_failed: "The roster build didn't finish.",
        review: "The roster is ready to review.",
        roster_submitting: "Submitting the roster.",
        roster_loading: "The roster is submitted and loading.",
        done: "The roster is submitted.",
    },
    participation: {
        choose: "Tick the files to merge.",
        building: "Merging the files.",
        build_failed: "The participation build didn't finish.",
        waiting_for_roster: "Waiting for the roster to be submitted.",
        roster_loading: "The roster is loading.",
        gate_waiting: "Waiting before participation can be submitted.",
        gate_blocked: "Participation can't be submitted right now.",
        override_required: "An override is needed to submit participation.",
        participation_ready: "Participation is ready to submit.",
        participation_submitting: "Submitting participation.",
        participation_loading: "Participation is submitted and loading.",
        done: "Participation is submitted.",
    },
};

const ACTIVE_STATUSES = ["queued", "running"];

// The statuses the Api writes while an upload is still loading: 3 validating, 5 validated and waiting for DataFactory.
const LOADING_UPLOAD_STATUSES = [3, 5];

const isLoading = (upload) => LOADING_UPLOAD_STATUSES.includes(upload?.status);

const POLLING_STEPS = ["building", "roster_submitting", "roster_loading", "gate_waiting", "participation_submitting", "participation_loading"];

const GATE_STEPS = {
    loading: "roster_loading",
    waiting: "gate_waiting",
    unlocked: "participation_ready",
    override_required: "override_required",
};

const SYMBOLIC_CODE = /^[A-Z][A-Z0-9_]+$/;

const pad = (value) => String(value).padStart(2, "0");

const listOf = (value) => (Array.isArray(value) ? value : []);

const firstText = (...candidates) => candidates.find((value) => typeof value === "string" && value.trim()) || "";

const lowerFirst = (text) => (text ? text.charAt(0).toLowerCase() + text.slice(1) : "");

export const formatCount = (value) => (Number.isFinite(value) ? value.toLocaleString("en-US") : "");

const plural = (count, word) => `${formatCount(count)} ${word}${count === 1 ? "" : "s"}`;

export const formatSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes < 0) return "";
    if (bytes < KB) return `${bytes} B`;
    if (bytes < MB) return `${(bytes / KB).toFixed(1)} KB`;
    if (bytes < GB) return `${(bytes / MB).toFixed(1)} MB`;
    return `${(bytes / GB).toFixed(1)} GB`;
};

export const formatDuration = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "";
    if (seconds > 0 && seconds < 1) return "< 1 s";
    const total = Math.round(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const rest = total % 60;
    if (hours > 0) return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
    if (minutes > 0) return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
    return `${rest} s`;
};

export const countdownText = (seconds) => {
    if (!Number.isFinite(seconds)) return "";
    const total = Math.max(0, Math.ceil(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const rest = total % 60;
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
};

export const remainingSeconds = (secondsRemaining, elapsedMs) =>
    Number.isFinite(secondsRemaining) ? Math.max(0, secondsRemaining - Math.max(0, elapsedMs) / 1000) : null;

export const sentenceCase = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : "");

export const breadcrumbOf = (path) => {
    const segments = (path || "").split("/").filter(Boolean);
    return [
        { label: ROOT_LABEL, path: "" },
        ...segments.map((name, index) => ({ label: name, path: segments.slice(0, index + 1).join("/") })),
    ];
};

export const untick = (ticks, path) => {
    const { [path]: unticked, ...rest } = ticks;
    return rest;
};

export const toggleTick = (ticks, file) =>
    ticks[file.path] ? untick(ticks, file.path) : { ...ticks, [file.path]: { path: file.path, name: file.name } };

export const tickedPaths = (ticks) => Object.keys(ticks).sort();

export const participationStatus = (ticks = {}) => {
    const count = Object.keys(ticks).length;
    return { count, canBuild: count > 0 };
};

export const readRosterObjects = (location) =>
    listOf(location?.roster_objects)
        .filter((object) => object && typeof object.key === "string" && object.key)
        .map((object) => ({ key: object.key, label: firstText(object.label, object.key) }));

export const objectLabel = (objects, key) => listOf(objects).find((object) => object.key === key)?.label || key || "";

// A row fills itself only when the folder holds exactly one file for its object; with none or several the user decides.
export const prefillRoster = (objects, files) =>
    Object.fromEntries(listOf(objects).map(({ key }) => {
        const matches = listOf(files).filter((file) => file.object === key);
        return [key, matches.length === 1 ? [matches[0].path] : []];
    }));

// A refresh keeps the picks whose files are still in the folder; a row left with none fills as it does when the folder opens.
export const refreshRosterPicks = (objects, picks, files) => {
    const present = new Set(listOf(files).map((file) => file.path));
    const filled = prefillRoster(objects, files);
    return Object.fromEntries(listOf(objects).map(({ key }) => {
        const kept = listOf(picks?.[key]).filter((path) => present.has(path));
        return [key, kept.length > 0 ? kept : filled[key]];
    }));
};

export const optionsFor = (key, files) => [
    ...listOf(files).filter((file) => file.object === key),
    ...listOf(files).filter((file) => file.object !== key),
];

export const setPick = (picks, key, paths) => ({ ...picks, [key]: [...new Set(listOf(paths))].sort() });

export const rosterStatus = (objects, picks = {}) => {
    const total = listOf(objects).length;
    const filled = listOf(objects).filter((object) => listOf(picks[object.key]).length > 0).length;
    return { filled, total, canBuild: total > 0 && filled === total };
};

export const rosterRequestFiles = (objects, picks) =>
    listOf(objects).flatMap(({ key }) => listOf(picks?.[key]).map((path) => ({ path, object: key })));

export const participationRequestFiles = (ticks) => tickedPaths(ticks).map((path) => ({ path }));

export const rebuildFiles = (build) =>
    listOf(build?.inputs).map((input) => (input.object ? { path: input.path, object: input.object } : { path: input.path }));

const dataOf = (response) => response?.response_data ?? null;

export const readLocation = (response) => {
    const data = dataOf(response);
    return data && typeof data === "object" && !Array.isArray(data) ? data : null;
};

export const readListing = (response) => {
    const data = dataOf(response);
    return { folders: listOf(data?.folders), files: listOf(data?.files), truncated: Boolean(data?.truncated) };
};

export const readBuilds = (response) => listOf(dataOf(response));

export const readBuildDetail = (response) => {
    const data = dataOf(response);
    return data?.build ? { build: data.build, submission: data.submission || null } : null;
};

export const readStartedBuildId = (response) => firstText(dataOf(response)?.build_id);

export const canBuildHere = (location) => Boolean(location?.enabled && location?.can_build);

export const latestBuild = (builds, kind) => listOf(builds).find((build) => build?.kind === kind) || null;

export const isActiveBuild = (build) => ACTIVE_STATUSES.includes(build?.status);

const OTHER_FLOW = { roster: "participation", participation: "roster" };

// One build runs at a time, so the build that refuses a start can be the other flow's.
export const blockingFlow = (builds, kind) => {
    const other = OTHER_FLOW[kind];
    return other && isActiveBuild(latestBuild(builds, other)) ? other : null;
};

const readBody = (data) => {
    if (data && typeof data === "object") return data;
    if (typeof data !== "string") return null;
    const text = data.trim();
    if (!text.startsWith("{") && !text.startsWith("[")) return null;
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

const kindOfStatus = (status) => {
    if (status === 401 || status === 403) return "auth";
    if (status === 404) return "not_found";
    if (status === 502 || status === 503 || status === 504) return "gateway";
    return status >= 500 ? "server" : "rejected";
};

const genericMessage = (kind, status) => {
    switch (kind) {
        case "network": return "Can't reach the server. Check your connection and try again.";
        case "auth": return "Your session has expired. Sign in again to continue.";
        case "not_found": return "Not found.";
        case "gateway": return "The server didn't answer in time.";
        case "server": return `The server ran into a problem (${status}). Try again in a moment.`;
        case "rejected": return "The request wasn't accepted.";
        default: return "Something went wrong. Try again.";
    }
};

// Only the user_message and error_message of the Api's own envelope are ever shown, plus the message of each blocking item of a refusal,
// one per line. Plain text, HTML and problem+json bodies fall back to a message chosen by status, so a raw body never reaches the screen.
export const classifyApiError = (error) => {
    const response = error?.response;
    if (!response) {
        const kind = error?.isAxiosError || error?.request ? "network" : "unexpected";
        return { kind, status: 0, code: "", message: genericMessage(kind, 0), columns: [], uploads: [] };
    }

    const kind = kindOfStatus(response.status);
    const body = readBody(response.data);
    const envelope = body && !Array.isArray(body) ? body : null;
    const data = envelope?.response_data;
    const details = data && typeof data === "object" && !Array.isArray(data) ? data : null;
    const legacyColumns = listOf(data).filter((column) => typeof column === "string");
    const columns = listOf(details?.unmatched_columns).length > 0 ? listOf(details.unmatched_columns) : legacyColumns;
    const named = firstText(listOf(envelope?.errors)[0]?.error_code, details?.code);
    const code = SYMBOLIC_CODE.test(named) ? named : legacyColumns.length > 0 ? "UNMATCHED_COLUMNS" : "";
    const blocked = kind === "rejected" ? listOf(details?.blocking).map((item) => firstText(item?.message)).filter(Boolean) : [];
    const shown = kind === "auth" || kind === "not_found"
        ? ""
        : firstText(envelope?.user_message, listOf(envelope?.errors)[0]?.error_message);

    return {
        kind,
        status: response.status,
        code,
        message: blocked.length > 0 ? blocked.join("\n") : shown || genericMessage(kind, response.status),
        columns,
        uploads: listOf(details?.uploads),
    };
};

// A submit or a build start that may or may not have reached the server: read the build again to find out.
export const isUnknownOutcome = (failure) =>
    failure.kind === "network" || failure.kind === "gateway" || failure.code === "SUBMIT_OUTCOME_UNKNOWN";

export const buildProgress = (build) => {
    const stages = stagesOf(build);
    if (!build || build.status === "queued") return { index: 0, failed: false };
    if (build.status === "succeeded") return { index: stages.length, failed: false };
    const timings = build.stage_seconds || {};
    const unfinished = stages.findIndex((stage, position) => position > 0 && !(stage.timing in timings));
    const index = unfinished < 0 ? stages.length : unfinished;
    const failed = build.status !== "running";
    return { index: failed ? Math.min(index, stages.length - 1) : index, failed };
};

export const stepStates = (build) => {
    const { index, failed } = buildProgress(build);
    return stagesOf(build).map((stage, position) => ({
        id: stage.id,
        label: stage.label,
        seconds: stage.timing ? (build?.stage_seconds?.[stage.timing] ?? null) : null,
        state: position < index ? "done" : position === index ? (failed ? "error" : "active") : "pending",
    }));
};

export const buildFailure = (build) => {
    const error = build?.error;
    const where = [
        error?.object && `object ${error.object}`,
        error?.column && `column ${error.column}`,
        Number.isFinite(error?.row) && `row ${error.row}`,
    ].filter(Boolean).join(", ");
    switch (build?.status) {
        case "interrupted": return { message: "The server restarted while the build was running.", code: "", where: "" };
        case "stale": return { message: "The build stopped reporting progress, so it was marked as stalled.", code: "", where: "" };
        default: return { message: firstText(error?.message) || "The build failed.", code: firstText(error?.code), where };
    }
};

const gateStep = (gate) => {
    if (!gate) return "gate_blocked";
    if (gate.state === "blocked" && gate.override_allowed) return "override_required";
    return GATE_STEPS[gate.state] || "gate_blocked";
};

// A roster build has no gate, so a submitted part is done only once its own upload has stopped loading.
const rosterStep = (submission) => {
    const roster = submission?.roster;
    if (roster?.state === "submitting") return "roster_submitting";
    if (roster?.state !== "submitted") return "review";
    return isLoading(roster.upload) ? "roster_loading" : "done";
};

// A participation build goes in after the roster submitted from Prime for its drop; with none, it waits for one.
const participationStep = (submission) => {
    const participation = submission?.participation;
    if (participation?.state === "submitted") return isLoading(participation.upload) ? "participation_loading" : "done";
    if (participation?.state === "submitting") return "participation_submitting";
    return submission?.roster ? gateStep(submission.gate) : "waiting_for_roster";
};

const stepOf = (build, submission) => {
    if (!build) return "choose";
    if (isActiveBuild(build)) return "building";
    if (build.status !== "succeeded") return "build_failed";
    return build.kind === "participation" ? participationStep(submission) : rosterStep(submission);
};

const submitAccess = (location, submission) => {
    if (location?.stage === "shadow") return { canSubmit: false, submitNote: SUBMIT_NOTES.shadow };
    if (submission?.submit_enabled === false) return { canSubmit: false, submitNote: SUBMIT_NOTES.switchedOff };
    if (!submission?.can_submit) {
        return { canSubmit: false, submitNote: location?.can_submit === false ? SUBMIT_NOTES.role : SUBMIT_NOTES.build };
    }
    return { canSubmit: true, submitNote: "" };
};

export const deriveView = (location, detail) => {
    const step = stepOf(detail?.build, detail?.submission);
    const built = step !== "choose" && step !== "building" && step !== "build_failed";
    return {
        step,
        built,
        polls: POLLING_STEPS.includes(step),
        ...(built ? submitAccess(location, detail.submission) : { canSubmit: false, submitNote: "" }),
    };
};

export const announcementFor = (flow, phase, step) => {
    if (phase === "ready") return STEP_ANNOUNCEMENTS[flow]?.[step] || "";
    return phase === "unavailable" ? FLOWS[flow]?.unavailable || "" : PHASE_ANNOUNCEMENTS[phase] || "";
};

export const loadStatusText = (upload) => {
    if (!upload) return "";
    if (isLoading(upload)) return "Still loading";
    const errors = listOf(upload.summaries).slice(-1)[0]?.errors;
    switch (upload.status) {
        case 1:
        case 2:
            if (upload.loaded === false) return "Rejected before it loaded";
            if (upload.status === 1) return "Loaded";
            return Number.isFinite(errors) && errors > 0 ? `Loaded with ${plural(errors, "row error")}` : "Loaded with row errors";
        case 0: return "The load failed";
        default: return upload.status_name || "Status unknown";
    }
};

export const uploadSummaryText = (upload) => {
    if (!upload) return "";
    return [Number.isFinite(upload.upload_id) && `Upload #${upload.upload_id}`, loadStatusText(upload)].filter(Boolean).join(". ");
};

export const pairedRosterText = (roster) => {
    if (!roster) return "";
    const id = roster.upload?.upload_id ?? roster.claim?.upload_id;
    const after = [firstText(roster.claim?.file_name), Number.isFinite(id) && `upload #${id}`].filter(Boolean).join(", ");
    const status = lowerFirst(loadStatusText(roster.upload));
    return `Goes in after ${after || "the roster"}${status ? `: ${status}` : ""}`;
};

export const rosterFirstText = (dropLabel) => (dropLabel ? `Submit the ${dropLabel} roster first.` : "Submit the roster first.");

const HEADER_CHECK_TEXT = { ok: "Header check passed", not_run: "Header check not run" };

// Unexpected columns also set blocks_submit, but a submit can ignore them; a failed check is the one that stops it.
// A submit runs the check again and its claim keeps what it ignored, so a submitted part reports that, not the build-time check.
export const headerCheckText = (check, part) => {
    const submitted = part?.state === "submitted";
    const ignored = listOf(part?.claim?.ignored_unmatched_columns).length;
    if (submitted && ignored > 0) return `${plural(ignored, "unexpected column")}, ignored at submit`;
    if (!check) return HEADER_CHECK_TEXT.not_run;
    const unexpected = listOf(check.unexpected_columns).length;
    if (unexpected > 0) {
        const count = plural(unexpected, "unexpected column");
        return submitted ? count : `${count}. The submit will ask you to ignore them`;
    }
    if (check.state === "failed" || check.blocks_submit) return "The header check found a problem that will stop the submit";
    return HEADER_CHECK_TEXT[check.state] || `Header check: ${String(check.state || "not run").replace(/_/g, " ")}`;
};

export const countsText = (output) => {
    const rows = `${formatCount(output?.rows)} rows`;
    return Number.isFinite(output?.distinct_npis) ? `${rows}, ${formatCount(output.distinct_npis)} NPIs` : rows;
};

export const outputFileName = (build, kind) => {
    if (kind === "manifest") return `${build.build_id}.json`;
    return build.outputs?.find((output) => output.kind === kind)?.file_name || `${build.build_id}-${kind}.csv`;
};

export const uploadLabel = (upload) => {
    const id = Number.isFinite(upload?.upload_id) ? `#${upload.upload_id}` : "An upload";
    const status = firstText(upload?.status_name);
    return [id, firstText(upload?.file_name), status && `(${status})`].filter(Boolean).join(" ");
};

export const isValidOverrideReason = (reason) => {
    const length = (reason || "").trim().length;
    return length >= OVERRIDE_REASON_MIN && length <= OVERRIDE_REASON_MAX;
};

export const interveningIds = (gate) => listOf(gate?.intervening_uploads).map((upload) => upload.upload_id);

export const acknowledgedIds = (gate, acknowledged) =>
    interveningIds(gate).filter((id) => listOf(acknowledged).includes(id));

export const overrideReady = (gate, reason, acknowledged) =>
    isValidOverrideReason(reason) && acknowledgedIds(gate, acknowledged).length === interveningIds(gate).length;

export const columnsPreview = (columns, limit = COLUMNS_PREVIEW_LIMIT) => {
    const names = listOf(columns);
    const shown = names.slice(0, limit).join(", ");
    return names.length > limit ? `${shown} and ${names.length - limit} more` : shown;
};

export const submitConfirmation = ({ kind, output, columns, override }) => {
    if (columns) {
        return {
            title: "Columns not in the template",
            content: `The file has columns that aren't in the template: ${columnsPreview(columns)}. Ignore them and submit anyway?`,
            confirmText: "Ignore and submit",
        };
    }
    const roster = kind === "roster";
    const file = output?.file_name ? `${output.file_name} (${countsText(output)})` : "The file";
    const recorded = override ? " Your override reason is recorded with the submit." : "";
    return {
        title: roster ? "Submit the roster?" : "Submit participation?",
        content: `${file} goes in under your login. A submitted file can't be withdrawn.${recorded}`,
        confirmText: override ? "Override and submit" : roster ? "Submit roster" : "Submit participation",
    };
};
